import { createAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import pLimit from 'p-limit';

import workspaceService from '@app/libs/workspaceService';
import {
  DOCUMENT_PROGRESS__BULK_EXPIRY_DATE_CHANGE_FAILURE,
  DOCUMENT_PROGRESS__BULK_EXPIRY_DATE_CHANGE_REQUEST,
  DOCUMENT_PROGRESS__BULK_EXPIRY_DATE_CHANGE_SUCCESS,
  DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_FAILURE,
  DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_REQUEST,
  DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_SUCCESS,
  DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_FAILURE,
  DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_REQUEST,
  DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_SUCCESS,
  DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_FAILURE,
  DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_REQUEST,
  DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_SUCCESS,
  DOCUMENT_PROGRESS__REQUEST_CANCEL_FAILURE,
  DOCUMENT_PROGRESS__REQUEST_CANCEL_REQUEST,
  DOCUMENT_PROGRESS__REQUEST_CANCEL_SUCCESS,
  DOCUMENT_PROGRESS__SIGNING_REMIND_FAILURE,
  DOCUMENT_PROGRESS__SIGNING_REMIND_REQUEST,
  DOCUMENT_PROGRESS__SIGNING_REMIND_SUCCESS,
} from '@app/store/actionTypes';
import FailedDocumentError from '@app/store/documentProgress/FailedDocumentError';
import VisibleDocument from '@app/store/documents/VisibleDocument';
import { putDocumentsParticipantsCurrentExpiry } from '@app/store/transport-layer/document/documents/participants/current/expiry';
import { Dispatch } from '@app/store/types';

/**
 * 서명 입력 만료일을 변경한다.
 */
export function changeExpiryDate(documentId: string, datetime: string) {
  return {
    type: DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_REQUEST,
    payload: { documentId, datetime },
  };
}
export function changeExpiryDateSuccess() {
  return {
    type: DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_SUCCESS,
  };
}
export function changeExpiryDateFailure(error: AxiosError | FailedDocumentError) {
  return {
    type: DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_FAILURE,
    payload: error,
    error: true,
  };
}

/**
 * 서명 입력 만료일을 일괄 변경한다.
 */
export function changeBulkExpiryDate(payload: { documents: VisibleDocument[]; datetime: string }) {
  return {
    type: DOCUMENT_PROGRESS__BULK_EXPIRY_DATE_CHANGE_REQUEST,
    payload,
  };
}
export function changeBulkExpiryDateSuccess() {
  return {
    type: DOCUMENT_PROGRESS__BULK_EXPIRY_DATE_CHANGE_SUCCESS,
  };
}
export function changeBulkExpiryDateFailure(payload: Error) {
  return {
    type: DOCUMENT_PROGRESS__BULK_EXPIRY_DATE_CHANGE_FAILURE,
    payload,
    error: true,
  };
}

/**
 * 서명 요청 이메일을 재전송한다.
 */
export function remindSigning(documentId: string) {
  return {
    type: DOCUMENT_PROGRESS__SIGNING_REMIND_REQUEST,
    payload: {
      documentId,
    },
  };
}
export function remindSigningSuccess() {
  return {
    type: DOCUMENT_PROGRESS__SIGNING_REMIND_SUCCESS,
  };
}
export function remindSigningFailure(error: AxiosError | FailedDocumentError) {
  return {
    type: DOCUMENT_PROGRESS__SIGNING_REMIND_FAILURE,
    payload: error,
    error: true,
  };
}

/**
 * 서명 요청 이메일을 일괄 재전송한다.
 */
export function remindBulkSigning(payload: VisibleDocument[]) {
  return {
    type: DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_REQUEST,
    payload,
  };
}

export function remindBulkSigningSuccess() {
  return {
    type: DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_SUCCESS,
  };
}

export function remindBulkSigningFailure(payload: Error) {
  return {
    type: DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_FAILURE,
    payload,
    error: true,
  };
}

/**
 * 서명 요청을 취소한다.
 */
export function cancelRequest({
  documentId,
  message,
  isDocumentBlock,
}: {
  documentId: string;
  message: string;
  isDocumentBlock: boolean;
}) {
  return {
    type: DOCUMENT_PROGRESS__REQUEST_CANCEL_REQUEST,
    payload: { documentId, message, isDocumentBlock },
  };
}
export const cancelRequestSuccess = createAction(DOCUMENT_PROGRESS__REQUEST_CANCEL_SUCCESS);
export function cancelRequestFailure(error: AxiosError | FailedDocumentError) {
  return {
    type: DOCUMENT_PROGRESS__REQUEST_CANCEL_FAILURE,
    payload: error,
    error: true,
  };
}

/**
 * 서명 요청을 일괄 취소한다.
 */
export function cancelBulkRequest(payload: {
  documents: VisibleDocument[];
  message: string;
  isDocumentBlock: boolean;
}) {
  return {
    type: DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_REQUEST,
    payload,
  };
}

export function cancelBulkRequestSuccess() {
  return {
    type: DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_SUCCESS,
  };
}

export function cancelBulkRequestFailure(payload: Error) {
  return {
    type: DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_FAILURE,
    payload,
    error: true,
  };
}

export type DocumentProgressAction =
  | ReturnType<typeof cancelBulkRequest>
  | ReturnType<typeof cancelBulkRequestSuccess>
  | ReturnType<typeof cancelBulkRequestFailure>
  | ReturnType<typeof cancelRequest>
  | ReturnType<typeof cancelRequestSuccess>
  | ReturnType<typeof cancelRequestFailure>
  | ReturnType<typeof changeBulkExpiryDate>
  | ReturnType<typeof changeBulkExpiryDateSuccess>
  | ReturnType<typeof changeBulkExpiryDateFailure>
  | ReturnType<typeof changeExpiryDate>
  | ReturnType<typeof changeExpiryDateSuccess>
  | ReturnType<typeof changeExpiryDateFailure>
  | ReturnType<typeof remindBulkSigning>
  | ReturnType<typeof remindBulkSigningSuccess>
  | ReturnType<typeof remindBulkSigningFailure>
  | ReturnType<typeof remindSigning>
  | ReturnType<typeof remindSigningSuccess>
  | ReturnType<typeof remindSigningFailure>;

export const changeBulkExpiryDateThunk = (payload: {
  documents: VisibleDocument[];
  datetime: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(changeBulkExpiryDate(payload));

    try {
      const limit = pLimit(10);
      await Promise.all(
        payload.documents.map((document) => {
          return limit(() => {
            const workspaceId = workspaceService.getWorkspaceId();
            return putDocumentsParticipantsCurrentExpiry({
              documentId: document.documentId,
              datetime: payload.datetime,
              workspaceId,
            }).then(
              () => {
                dispatch(changeExpiryDateSuccess());
              },
              (error) => {
                dispatch(changeExpiryDateFailure(new FailedDocumentError(error.message, document)));
                return Promise.reject(error);
              }
            );
          });
        })
      );

      dispatch(changeBulkExpiryDateSuccess());
    } catch (error) {
      dispatch(changeBulkExpiryDateFailure(error));
    }
  };
};
